// Primitive 1: Actor — an Ed25519 public key. Nothing else.
// Primitive 2: Claim — an immutable, content-addressed statement signed by one actor.

import { sha256 } from './sha256.ts';

export type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
export type ActorId = string; // "ed25519:<base64url raw public key>"
export type ClaimId = string; // "c_<base64url sha-256 of the canonical unsigned claim>"
export type Audience = 'scope' | ActorId[];

export interface UnsignedClaim {
  author: ActorId;
  predicate: string;
  subject: ActorId | null;
  refs: ClaimId[];
  body: Json;
  audience: Audience;
  issuedAt: number;
  // v2: the scope this claim is addressed to. null = any scope. A scope refuses a claim bound elsewhere.
  scope: ActorId | null;
}

export interface Claim extends UnsignedClaim {
  id: ClaimId;
  sig: string;
}

export interface Actor {
  id: ActorId;
  privateKey: CryptoKey;
}

const enc = new TextEncoder();

// Web-standard base64url (btoa/atob), so the same code runs in browsers, Workers and Node.
export function b64u(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = '';
  for (const b of u8) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64u(s: string): Uint8Array<ArrayBuffer> {
  const b = atob(s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4));
  const out = new Uint8Array(new ArrayBuffer(b.length));
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
  return out;
}

// Deterministic serialisation: sorted keys, no whitespace. The bytes are the claim.
export function canonical(v: Json): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  const keys = Object.keys(v).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical((v as Record<string, Json>)[k] as Json)}`).join(',')}}`;
}

// The hashed form. `scope` enters the hash only when set: a v1 claim (no scope key) and a v2 claim with
// scope null are the same bytes, so every claim signed before v2 still verifies.
function unsignedOf(c: UnsignedClaim): Json {
  const u: Record<string, Json> = {
    author: c.author,
    predicate: c.predicate,
    subject: c.subject,
    refs: c.refs,
    body: c.body,
    audience: c.audience,
    issuedAt: c.issuedAt,
  };
  if (c.scope !== null && c.scope !== undefined) u.scope = c.scope;
  return u;
}

// Synchronous id: the pure SHA-256 lets restore paths recompute ids without awaiting.
export function idOfSync(c: UnsignedClaim): ClaimId {
  return `c_${b64u(sha256(enc.encode(canonical(unsignedOf(c)))))}`;
}

async function idOf(c: UnsignedClaim): Promise<ClaimId> {
  const digest = await crypto.subtle.digest('SHA-256', enc.encode(canonical(unsignedOf(c))));
  return `c_${b64u(digest)}`;
}

export async function newActor(): Promise<Actor> {
  const kp = (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, ['sign', 'verify'])) as CryptoKeyPair;
  const raw = (await crypto.subtle.exportKey('raw', kp.publicKey)) as ArrayBuffer;
  return { id: `ed25519:${b64u(raw)}`, privateKey: kp.privateKey };
}

export interface Draft {
  predicate: string;
  subject?: ActorId | null;
  refs?: ClaimId[];
  body?: Json;
  audience?: Audience;
  issuedAt: number;
  scope?: ActorId | null;
}

export function unsigned(author: ActorId, d: Draft): UnsignedClaim {
  return {
    author,
    predicate: d.predicate,
    subject: d.subject ?? null,
    refs: d.refs ?? [],
    body: d.body ?? {},
    audience: d.audience ?? 'scope',
    issuedAt: d.issuedAt,
    scope: d.scope ?? null,
  };
}

// v2.2 (L1/L2 at the in-process boundary, REDTEAM-3 L5): the bytes are the claim. A value JSON cannot carry
// (Infinity, NaN, undefined, a function, a class instance) would hash and sign as something else ({x: Infinity} and
// {x: null} share an id), so sign() refuses it and Scope.submit() treats it as unsigned.
export const MAX_JSON_DEPTH = 1000;
export function isJson(x: unknown, depth = 0, max = MAX_JSON_DEPTH): x is Json {
  if (depth > max) return false;
  if (x === null || typeof x === 'boolean' || typeof x === 'string') return true;
  if (typeof x === 'number') return Number.isFinite(x);
  if (Array.isArray(x)) {
    for (let i = 0; i < x.length; i++) if (!(i in x) || !isJson(x[i], depth + 1, max)) return false;
    return true;
  }
  if (typeof x === 'object') {
    const proto = Object.getPrototypeOf(x);
    if (proto !== Object.prototype && proto !== null) return false;
    return Object.values(x as object).every((y) => isJson(y, depth + 1, max));
  }
  return false;
}

// Every field of a claim is JSON of the right shape (the body is checked in full).
export function isJsonClaim(c: UnsignedClaim): boolean {
  return isJson(c.body) && typeof c.issuedAt === 'number' && Number.isFinite(c.issuedAt) && Array.isArray(c.refs) && c.refs.every((r) => typeof r === 'string');
}

export async function sign(actor: Actor, d: Draft): Promise<Claim> {
  const u = unsigned(actor.id, d);
  if (!isJsonClaim(u)) throw new TypeError('sign: the claim is not JSON (a non-finite number, undefined or a non-plain object); the bytes would not be the claim');
  const id = await idOf(u);
  const sig = await crypto.subtle.sign('Ed25519', actor.privateKey, enc.encode(id));
  return deepFreeze({ ...u, id, sig: b64u(sig) });
}

// A claim verifies anywhere from its bytes alone: recompute the id, check the signature against the author key.
export async function verify(c: Claim): Promise<boolean> {
  try {
    if (typeof c.author !== 'string' || !c.author.startsWith('ed25519:')) return false;
    if ((await idOf(c)) !== c.id) return false;
    const key = await crypto.subtle.importKey('raw', fromB64u(c.author.slice(8)), { name: 'Ed25519' }, false, ['verify']);
    return await crypto.subtle.verify('Ed25519', key, fromB64u(c.sig), enc.encode(c.id));
  } catch {
    return false;
  }
}

// Claims may embed other signed claims (e.g. a private message promoted into a case). The law verifies them too.
// v2: an embedded claim is recognised ONLY as the value of a "$claim" key, at any depth. No duck typing:
// an honest body that happens to carry id/sig/author fields is data, not a claim.
export const EMBED = '$claim';

export function embeddedClaims(v: Json): Claim[] {
  const out: Claim[] = [];
  const walk = (x: Json): void => {
    if (x === null || typeof x !== 'object') return;
    if (Array.isArray(x)) return x.forEach(walk);
    for (const [k, inner] of Object.entries(x)) {
      if (k === EMBED) out.push(inner as unknown as Claim);
      walk(inner);
    }
  };
  walk(v);
  return out;
}

export function deepFreeze<T>(o: T): T {
  if (o && typeof o === 'object' && !Object.isFrozen(o)) {
    Object.freeze(o);
    Object.values(o as object).forEach(deepFreeze);
  }
  return o;
}
